// Pure helpers for keyboard shortcuts: parsing, matching a KeyboardEvent, display formatting and
// the registry the "?" dialog (components/ui/ShortcutsHelp) reads. No React in here.
//
// Syntax of a shortcut ("spec"): chords separated by spaces, keys of a chord joined by "+".
//   "?"  "n"  "escape"  "mod+k" (⌘ on Mac, Ctrl elsewhere)  "shift+enter"  "alt+arrowup"
//   "g c" = press G, then C within a second
// Keys are KeyboardEvent.key lowercased ("arrowdown", "enter", "/", "1"). Aliases: cmd, ctrl,
// meta -> mod; option -> alt; esc -> escape; return -> enter; del -> delete; up/down/left/right
// -> arrow*; space -> " ". Shift is part of a chord only for letters and named keys: "?" is
// typed as Shift+/ but written "?".

const MODIFIER_ORDER = ["mod", "alt", "shift"];
const MODIFIER_ALIASES = {
  cmd: "mod",
  command: "mod",
  ctrl: "mod",
  control: "mod",
  meta: "mod",
  mod: "mod",
  option: "alt",
  opt: "alt",
  alt: "alt",
  shift: "shift",
};
const KEY_ALIASES = {
  esc: "escape",
  return: "enter",
  del: "delete",
  up: "arrowup",
  down: "arrowdown",
  left: "arrowleft",
  right: "arrowright",
  spacebar: "space",
  " ": "space",
  plus: "+",
};
const MODIFIER_KEYS = new Set(["shift", "control", "alt", "meta", "os", "altgraph", "capslock"]);

export const isMac = () =>
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad|ipod/i.test(navigator.userAgentData?.platform || navigator.platform || "");

const normalizeKey = (key) => {
  const lower = String(key).toLowerCase();
  return KEY_ALIASES[lower] ?? lower;
};

// "Mod+Shift+K" -> "mod+shift+k" (modifiers in a fixed order)
export const normalizeChord = (chord) => {
  const parts = String(chord)
    .split("+")
    .map((part) => part.trim())
    .filter(Boolean);
  // "mod++" (the plus key) splits into ["mod"]: keep the "+" as key
  if (String(chord).endsWith("++")) parts.push("+");
  const modifiers = new Set();
  let key = "";
  parts.forEach((part) => {
    const lower = part.toLowerCase();
    if (MODIFIER_ALIASES[lower] && parts.length > 1) modifiers.add(MODIFIER_ALIASES[lower]);
    else key = normalizeKey(part);
  });
  return [...MODIFIER_ORDER.filter((m) => modifiers.has(m)), key].join("+");
};

// "g c" -> ["g", "c"]; "mod+k" -> ["mod+k"]
export const parseSpec = (spec) =>
  String(spec).trim().split(/\s+/).filter(Boolean).map(normalizeChord);

export const normalizeSpec = (spec) => parseSpec(spec).join(" ");

// The chord a keydown stands for, in the same form as normalizeChord. null for a lone modifier.
export const eventToChord = (event) => {
  if (!event?.key) return null;
  let key = normalizeKey(event.key);
  if (MODIFIER_KEYS.has(key)) return null;
  // Option+C types "ç" on a Mac: read the physical key instead
  if (event.altKey && event.code) {
    if (event.code.startsWith("Key")) key = event.code.slice(3).toLowerCase();
    else if (event.code.startsWith("Digit")) key = event.code.slice(5);
  }
  const modifiers = [];
  if (event.metaKey || event.ctrlKey) modifiers.push("mod");
  if (event.altKey) modifiers.push("alt");
  if (event.shiftKey && (key.length > 1 || /^[a-z]$/.test(key))) modifiers.push("shift");
  return [...modifiers, key].join("+");
};

const MAC_LABELS = {
  mod: "⌘",
  alt: "⌥",
  shift: "⇧",
  enter: "↵",
  escape: "Esc",
  backspace: "⌫",
  delete: "⌦",
  tab: "⇥",
  space: "Space",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
};
const PC_LABELS = {
  ...MAC_LABELS,
  mod: "Ctrl",
  alt: "Alt",
  shift: "Shift",
  enter: "Enter",
  backspace: "Backspace",
  delete: "Canc",
  tab: "Tab",
};

const keyLabel = (key, mac) => {
  const labels = mac ? MAC_LABELS : PC_LABELS;
  if (labels[key]) return labels[key];
  if (/^f\d{1,2}$/.test(key)) return key.toUpperCase();
  // named keys ("pageup", "home") are capitalised; letters and ready labels ("⌘c") upper-cased
  return /^[a-z]{2,}$/.test(key) ? key.charAt(0).toUpperCase() + key.slice(1) : key.toUpperCase();
};

/**
 * Display labels of a shortcut, one array of keys per chord.
 * "mod+shift+k" -> [["⌘", "⇧", "K"]] on a Mac, [["Ctrl", "Shift", "K"]] elsewhere.
 * An array of keys is one chord: ["mod", "c"] -> [["⌘", "C"]].
 * A string with no "+" nor spaces that is not a known key ("⌘C") is shown as is.
 */
export const formatHotkey = (spec, { mac = isMac() } = {}) => {
  if (Array.isArray(spec)) {
    return [
      spec.map((key) =>
        keyLabel(MODIFIER_ALIASES[String(key).toLowerCase()] ?? normalizeKey(key), mac)
      ),
    ];
  }
  return parseSpec(spec).map((chord) => {
    // the "+" key itself ("mod++", "+")
    const plusKey = chord === "+" || chord.endsWith("++");
    const keys = plusKey
      ? [...chord.slice(0, -2).split("+").filter(Boolean), "+"]
      : chord.split("+");
    return keys.map((key) => keyLabel(key, mac));
  });
};

// Compact text for menus: "⌘⇧K" on a Mac, "Ctrl+Shift+K" elsewhere, "G C" for a sequence
export const hotkeyToText = (spec, options) => {
  const mac = options?.mac ?? isMac();
  return formatHotkey(spec, { mac })
    .map((chord) => chord.join(mac ? "" : "+"))
    .join(" ");
};

// Targets where plain keys belong to the user typing (fields, and the typeahead of open
// selects, listboxes and menus)
const TYPING_SELECTOR = [
  "input",
  "textarea",
  "select",
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[role="textbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="menu"]',
  '[role="menuitem"]',
].join(", ");
export const isTypingTarget = (target) =>
  target instanceof Element && (target.isContentEditable || !!target.closest(TYPING_SELECTOR));

// Sequence started by any useHotkeys instance ("g" of "g n"), shared so that a page binding the
// plain second key ("n") leaves it to the sequence: { at, nexts: Set<chord>, owner }
export const SEQUENCE_TIMEOUT_MS = 1000;
let pendingSequence = null;
export const setPendingSequence = (value) => {
  pendingSequence = value;
};
export const getPendingSequence = () => pendingSequence;
/** True when this keydown completes a sequence started by another listener (e.g. "g n"). */
export const continuesPendingSequence = (event, owner = null) => {
  const pending = pendingSequence;
  if (!pending || pending.owner === owner) return false;
  if (Date.now() - pending.at >= SEQUENCE_TIMEOUT_MS) return false;
  const chord = eventToChord(event);
  return !!chord && pending.nexts.has(chord);
};

// ---------------------------------------------------------------------------------------------
// Registry: shortcuts shown in the "?" dialog. useHotkeys registers its bindings that carry a
// description; a page can also document shortcuts it handles itself with registerShortcut.

let entries = new Map();
let snapshot = [];
let nextId = 1;
const listeners = new Set();

const emit = () => {
  snapshot = [...entries.values()];
  listeners.forEach((listener) => listener());
};

/**
 * Documents a shortcut in the "?" dialog. Returns the function that removes it.
 * @param {{ keys?: string|string[], keysLabel?: string, description: string, group?: string }} entry
 *   keys: a spec ("mod+k", "g c") or a list of alternatives (["j", "arrowdown"]); keysLabel: a
 *   text shown instead of keys ("Tasto destro"); description and group are translated texts.
 */
export const registerShortcut = (entry) => {
  const id = nextId++;
  entries.set(id, { ...entry, id });
  emit();
  return () => {
    if (entries.delete(id)) emit();
  };
};

export const subscribeShortcuts = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getShortcutsSnapshot = () => snapshot;

// Opening the dialog from anywhere (a "?" button in the navbar, a menu item)
const helpListeners = new Set();
export const openShortcutsHelp = () => helpListeners.forEach((listener) => listener());
export const onOpenShortcutsHelp = (listener) => {
  helpListeners.add(listener);
  return () => helpListeners.delete(listener);
};

// Test helper
export const resetShortcutsRegistry = () => {
  entries = new Map();
  emit();
};
